// src/App.js

import React, { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { App as AntdApp, ConfigProvider } from 'antd'
import itIT from 'antd/locale/it_IT'
import PropTypes from 'prop-types'
import ErrorBoundary from '@components/Common/Error/ErrorBoundary'
import PageLoader from '@components/Common/Loading/PageLoader'
import StoreProvider from '@providers/StoreProvider'
import useAuthStore from '@stores/authStore'
import notification from '@services/notification'
import { CondolenceWallContext, defaultSetting } from './context/condolencewall-context'
import '@services/http'
import './styles/global.less'

// Lazy loading...
const GalleryPage = lazy(() => import('@pages/Public/GalleryPage'))
const ObituaryDetailPage = lazy(() => import('@pages/Public/ObituaryDetailPage'))
const MessageSubmittedPage = lazy(() => import('@pages/Public/MessageSubmittedPage'))
const EmbedGalleryPage = lazy(() => import('@pages/Public/EmbedGalleryPage'))
const LoginPage = lazy(() => import('@pages/Agency/LoginPage'))
const DashboardPage = lazy(() => import('@pages/Agency/DashboardPage'))
const ObituariesPage = lazy(() => import('@pages/Agency/ObituariesPage'))
const MessagesPage = lazy(() => import('@pages/Agency/MessagesPage'))
const SettingsPage = lazy(() => import('@pages/Agency/SettingsPage'))
const ProfilePage = lazy(() => import('@pages/Agency/ProfilePage'))
const LayoutTemplatesPage = lazy(() => import('@pages/Agency/LayoutTemplatesPage'))
const LayoutTemplateEditorPage = lazy(() => import('@pages/Agency/LayoutTemplateEditorPage'))
const AgenciesPage = lazy(() => import('@pages/Admin/AgenciesPage'))
const AdminCreateObituaryPage = lazy(() => import('@pages/Admin/AdminCreateObituaryPage'))

// PAGINE STATICHE
const CookiePolicy = lazy(() => import('@pages/Static/CookiePolicy'))
const PrivacyPolicy = lazy(() => import('@pages/Static/PrivacyPolicy'))
const TermsOfUse = lazy(() => import('@pages/Static/TermsOfUse'))

// Route Guards
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, isLoading, isInitialized } = useAuthStore()

    if (!isInitialized || isLoading) return <PageLoader />
    if (!isAuthenticated()) return <Navigate to="/agency/login" replace />

    return children
}

ProtectedRoute.propTypes = { children: PropTypes.node.isRequired }

const AdminRoute = ({ children }) => {
    const { isSuperAdmin, isLoading, isInitialized } = useAuthStore()

    if (!isInitialized || isLoading) return <PageLoader />
    if (!isSuperAdmin()) return <Navigate to="/agency/dashboard" replace />

    return children
}

AdminRoute.propTypes = { children: PropTypes.node.isRequired }

function AppRoutes() {
    return (
        <Suspense fallback={<PageLoader />}>
            <Routes>
                <Route path="/" element={<GalleryPage />} />
                <Route path="/obituary/:id" element={<ObituaryDetailPage />} />
                <Route path="/submitted" element={<MessageSubmittedPage />} />
                <Route path="/embed/:alias" element={<EmbedGalleryPage />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-use" element={<TermsOfUse />} />
                <Route path="/agency/login" element={<LoginPage />} />
                <Route path="/agency/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                <Route path="/agency/obituaries" element={<ProtectedRoute><ObituariesPage /></ProtectedRoute>} />
                <Route path="/agency/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
                <Route path="/agency/templates" element={<ProtectedRoute><LayoutTemplatesPage /></ProtectedRoute>} />
                <Route path="/agency/templates/:id" element={<ProtectedRoute><LayoutTemplateEditorPage /></ProtectedRoute>} />
                <Route path="/agency/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                <Route path="/agency/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                <Route path="/admin/agencies" element={<AdminRoute><AgenciesPage /></AdminRoute>} />
                <Route path="/admin/obituaries/create" element={<AdminRoute><AdminCreateObituaryPage /></AdminRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    )
}

function AppContent() {
    const staticFunctionInitialized = useRef(false)
    const { message, modal } = AntdApp.useApp()

    useEffect(() => {
        if (!staticFunctionInitialized.current) {
            notification.init(message, modal)
            staticFunctionInitialized.current = true
        }
    }, [message, modal])

    return (
        <StoreProvider>
            <AppRoutes />
        </StoreProvider>
    )
}

function Fallback({ error }) {
    return <div>Qualcosa è andato storto: {error.message}</div>
}

function App() {
    const [aSettings, setASettings] = useState(defaultSetting)
    const [notifications, setNotifications] = useState([])

    const handleSettingsChange = (nPref) => {
        setASettings({ ...aSettings, ...nPref })
    }

    const handleNotification = (nNotification) => {
        setNotifications(prev => [...prev, nNotification])
    }

    // Le notifiche vengono gestite dal contesto, ma l'UI sarà gestita da AntdApp
    // Non serve più useEffect per le notifiche perché useAppNotification le mostra direttamente

    return (
        <CondolenceWallContext.Provider
            value={{
                setting: aSettings,
                onChange: handleSettingsChange,
                addNotification: handleNotification
            }}
        >
            <ErrorBoundary FallbackComponent={Fallback}>
                <BrowserRouter>
                    <ConfigProvider locale={itIT}>
                        <AntdApp>
                            <AppContent />
                        </AntdApp>
                    </ConfigProvider>
                </BrowserRouter>
            </ErrorBoundary>
        </CondolenceWallContext.Provider>
    )
}

export default App